/**
 * Buckets trip steps into the three journey phases — `departure`, `destination`,
 * and `return` — based on each step's `phase` field.
 *
 * The grouping is a partition: every input step is placed into exactly one phase
 * bucket, with no drops and no duplicates. The returned array contains only the
 * non-empty phases, ordered as a subsequence of `[departure, destination, return]`
 * (chronological journey order).
 *
 * Step order within each phase is preserved from the input (chronological sorting
 * is applied separately by `sortStepsChronologically`).
 *
 * This function is total: it never throws. Non-object/falsy entries and steps whose
 * `phase` is not one of the three known phase ids are ignored (they are not valid
 * timeline steps), so they neither appear in the output nor cause an error.
 *
 * @typedef {'departure'|'destination'|'return'} PhaseId
 *
 * @param {Array<{ phase: PhaseId }>} steps - Trip steps, each tagged with a phase
 * @returns {Array<{ id: PhaseId, steps: Array<Object> }>} Non-empty phase buckets
 *   in `[departure, destination, return]` order
 */
export function groupIntoPhases(steps) {
  const buckets = {
    departure: [],
    destination: [],
    return: [],
  };

  if (Array.isArray(steps)) {
    for (const step of steps) {
      if (step && step.phase === 'departure') {
        buckets.departure.push(step);
      } else if (step && step.phase === 'destination') {
        buckets.destination.push(step);
      } else if (step && step.phase === 'return') {
        buckets.return.push(step);
      }
    }
  }

  // Fixed `[departure, destination, return]` order; only non-empty phases are returned.
  const ordered = [
    { id: 'departure', steps: buckets.departure },
    { id: 'destination', steps: buckets.destination },
    { id: 'return', steps: buckets.return },
  ];

  return ordered.filter((phase) => phase.steps.length > 0);
}
